// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type EmailTemplateType =
    | 'account_creation'
    | 'password_reset'
    | 'password_reset_confirmation'
    | 'order_creation'
    | 'order_cancellation';

export interface EmailTemplateRecord {
    id: number;
    type: EmailTemplateType;
    subject: string;
    body: string;
    created_at?: string | null;
    updated_at?: string | null;
}

export interface EmailTemplateFormState {
    type: EmailTemplateType;
    subject: string;
    body: string;
}

// ---------------------------------------------------------------------------
// Template meta (labels, descriptions, available variables)
// ---------------------------------------------------------------------------

export interface TemplateMeta {
    type: EmailTemplateType;
    label: string;
    description: string;
    icon: string;
    variables: Array<{ key: string; description: string }>;
}

export const TEMPLATE_META: TemplateMeta[] = [
    {
        type: 'account_creation',
        label: 'Account Creation',
        description: 'Sent when a new customer registers an account.',
        icon: '👤',
        variables: [
            { key: '{{customer_name}}', description: 'Full name of the customer' },
            { key: '{{customer_email}}', description: 'Customer email address' },
            { key: '{{login_url}}', description: 'Link to the login page' },
            { key: '{{store_name}}', description: 'Name of your store' },
        ],
    },
    {
        type: 'password_reset',
        label: 'Password Reset',
        description: 'Sent when a customer requests a password reset link.',
        icon: '🔑',
        variables: [
            { key: '{{customer_name}}', description: 'Full name of the customer' },
            { key: '{{reset_url}}', description: 'Password reset link (expires in 60 min)' },
            { key: '{{store_name}}', description: 'Name of your store' },
        ],
    },
    {
        type: 'password_reset_confirmation',
        label: 'Password Reset Confirmation',
        description: 'Sent after a customer successfully resets their password.',
        icon: '✅',
        variables: [
            { key: '{{customer_name}}', description: 'Full name of the customer' },
            { key: '{{login_url}}', description: 'Link to the login page' },
            { key: '{{store_name}}', description: 'Name of your store' },
        ],
    },
    {
        type: 'order_creation',
        label: 'Order Creation',
        description: 'Sent after a successful payment with full order details.',
        icon: '🛍️',
        variables: [
            { key: '{{customer_name}}', description: 'Full name of the customer' },
            { key: '{{order_id}}', description: 'Unique order identifier' },
            { key: '{{order_date}}', description: 'Date the order was placed' },
            { key: '{{order_total}}', description: 'Total amount charged' },
            { key: '{{order_items}}', description: 'HTML table of ordered items' },
            { key: '{{shipping_address}}', description: 'Shipping address' },
            { key: '{{order_url}}', description: 'Link to the order details page' },
            { key: '{{store_name}}', description: 'Name of your store' },
        ],
    },
    {
        type: 'order_cancellation',
        label: 'Order Cancellation',
        description: 'Sent when an order is cancelled.',
        icon: '❌',
        variables: [
            { key: '{{customer_name}}', description: 'Full name of the customer' },
            { key: '{{order_id}}', description: 'Unique order identifier' },
            { key: '{{order_date}}', description: 'Date the order was placed' },
            { key: '{{cancellation_reason}}', description: 'Reason for cancellation (if available)' },
            { key: '{{support_url}}', description: 'Link to customer support' },
            { key: '{{store_name}}', description: 'Name of your store' },
        ],
    },
];

// ---------------------------------------------------------------------------
// Form helpers
// ---------------------------------------------------------------------------

export const emptyTemplateForm = (type: EmailTemplateType): EmailTemplateFormState => ({
    type,
    subject: '',
    body: '',
});

export const templateToFormState = (template: EmailTemplateRecord): EmailTemplateFormState => ({
    type: template.type,
    subject: template.subject || '',
    body: template.body || '',
});

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

type ValidationResult<T> =
    | { valid: true; data: T }
    | { valid: false; message: string };

export const validateTemplateForm = (
    form: EmailTemplateFormState,
): ValidationResult<EmailTemplateFormState> => {
    const subject = form.subject.trim();
    const body = form.body.replace(/<[^>]*>/g, '').trim(); // strip HTML tags for empty check

    if (!subject) {
        return { valid: false, message: 'Subject line is required.' };
    }

    if (subject.length > 255) {
        return { valid: false, message: 'Subject must be 255 characters or fewer.' };
    }

    if (!body) {
        return { valid: false, message: 'Email body cannot be empty.' };
    }

    return {
        valid: true,
        data: {
            type: form.type,
            subject: subject,
            body: form.body,
        },
    };
};

// ---------------------------------------------------------------------------
// API extraction helpers
// ---------------------------------------------------------------------------

const asRecord = (value: unknown): Record<string, any> | null => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return null;
    }
    return value as Record<string, any>;
};

const extractApiPayload = (response: any): unknown => {
    const data = response?.data;
    const record = asRecord(data);

    if (record && 'data' in record) {
        return record.data;
    }

    return data ?? response;
};

const extractItemsArray = <T>(payload: unknown): T[] => {
    if (Array.isArray(payload)) {
        return payload as T[];
    }

    const record = asRecord(payload);

    if (record && Array.isArray(record.data)) {
        return record.data as T[];
    }

    return [];
};

export const extractTemplateCollection = (response: any): EmailTemplateRecord[] => {
    const payload = extractApiPayload(response);
    return extractItemsArray<EmailTemplateRecord>(payload);
};

export const extractSingleTemplate = (response: any): EmailTemplateRecord | null => {
    const payload = extractApiPayload(response);
    const record = asRecord(payload);

    if (record && 'id' in record) {
        return record as unknown as EmailTemplateRecord;
    }

    return null;
};

export const extractApiErrorMessage = (err: any, fallback: string): string => {
    const message =
        err?.response?.data?.messages ||
        err?.response?.data?.message ||
        err?.message;

    if (Array.isArray(message)) {
        return message.join(', ');
    }

    return typeof message === 'string' && message.trim() ? message : fallback;
};
